import { createContext, type ReactNode } from 'react'
import { toast } from 'sonner'
import * as unionsService from '~/services/unions'
import type {
  CreateUnionRequest,
  Union,
  UpdateUnionRequest,
} from '~/types/union'

interface UnionsContextType {
  listUnions: (federationId?: number) => Promise<Union[]>
  getUnion: (id: number) => Promise<Union | null>
  createUnion: (data: CreateUnionRequest) => Promise<Union | null>
  updateUnion: (id: number, data: UpdateUnionRequest) => Promise<Union | null>
  deleteUnion: (id: number) => Promise<boolean>
}

export const UnionsContext = createContext<UnionsContextType | undefined>(
  undefined
)

interface UnionsProviderProps {
  children: ReactNode
}

export function UnionsProvider({ children }: UnionsProviderProps) {
  const listUnions = async (federationId?: number): Promise<Union[]> => {
    try {
      const response = await unionsService.listUnions(federationId)

      if (response.success) {
        return response.data
      } else {
        toast.error(response.error || 'Erro ao listar sindicatos')
        return []
      }
    } catch (error) {
      toast.error('Erro ao listar sindicatos')
      console.error('List unions error:', error)
      return []
    }
  }

  const getUnion = async (id: number): Promise<Union | null> => {
    try {
      const response = await unionsService.getUnion(id)

      if (response.success) {
        return response.data
      } else {
        toast.error(response.error || 'Erro ao buscar sindicato')
        return null
      }
    } catch (error) {
      toast.error('Erro ao buscar sindicato')
      console.error('Get union error:', error)
      return null
    }
  }

  const createUnion = async (
    data: CreateUnionRequest
  ): Promise<Union | null> => {
    try {
      const response = await unionsService.createUnion(data)

      if (response.success) {
        toast.success('Sindicato criado com sucesso!')
        return response.data.sindicato
      } else {
        toast.error(response.error || 'Erro ao criar sindicato')
        return null
      }
    } catch (error) {
      toast.error('Erro ao criar sindicato')
      console.error('Create union error:', error)
      return null
    }
  }

  const updateUnion = async (
    id: number,
    data: UpdateUnionRequest
  ): Promise<Union | null> => {
    try {
      const response = await unionsService.updateUnion(id, data)

      if (response.success) {
        toast.success('Sindicato atualizado com sucesso!')
        return response.data.sindicato
      } else {
        toast.error(response.error || 'Erro ao atualizar sindicato')
        return null
      }
    } catch (error) {
      toast.error('Erro ao atualizar sindicato')
      console.error('Update union error:', error)
      return null
    }
  }

  const deleteUnion = async (id: number): Promise<boolean> => {
    try {
      const response = await unionsService.deleteUnion(id)

      if (response.success) {
        toast.success('Sindicato excluído com sucesso!')
        return true
      } else {
        toast.error(response.error || 'Erro ao excluir sindicato')
        return false
      }
    } catch (error) {
      toast.error('Erro ao excluir sindicato')
      console.error('Delete union error:', error)
      return false
    }
  }

  return (
    <UnionsContext.Provider
      value={{
        listUnions,
        getUnion,
        createUnion,
        updateUnion,
        deleteUnion,
      }}
    >
      {children}
    </UnionsContext.Provider>
  )
}
